import type { StandardErrorResponse } from '@/types';
import { useAuthStore } from '@teletienda/shared-auth-ui';
import { SHORT_TO_LOCALE } from '@teletienda/shared-utils-lib';
import i18n from '@/i18n/config';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string | StandardErrorResponse['error'];
  message?: string;
}

/**
 * Retry configuration for API requests
 */
interface RetryConfig {
  /** Maximum number of retry attempts (default: 3) */
  limit?: number;
  /**
   * HTTP methods to retry (default: GET, HEAD). A write is not idempotent anywhere on this
   * platform — Embedded Signup's code is single-use, payments go through the same client — so a
   * caller that knows its endpoint is idempotent opts in explicitly.
   */
  methods?: string[];
  /** HTTP status codes to retry (default: 408, 429, 500, 502, 503, 504) */
  statusCodes?: number[];
  /** Maximum delay between retries in ms (default: 5000) */
  backoffLimit?: number;
  /** Initial delay in ms (default: 1000) */
  initialDelay?: number;
}

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  limit: 3,
  methods: ['GET', 'HEAD'],
  statusCodes: [408, 429, 500, 502, 503, 504],
  backoffLimit: 5000,
  initialDelay: 1000,
};

/**
 * Fetch with automatic retry logic
 * Implements exponential backoff for transient errors
 *
 * @param url - Request URL
 * @param init - Fetch init options
 * @param config - Retry configuration
 * @returns Response promise
 */
async function fetchWithRetry(
  url: string,
  init: RequestInit = {},
  config: RetryConfig = {}
): Promise<Response> {
  const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  const methodIsRetryable = retryConfig.methods.includes((init.method || 'GET').toUpperCase());
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retryConfig.limit; attempt++) {
    try {
      const response = await fetch(url, init);

      // Check if we should retry based on status code
      const shouldRetry =
        retryConfig.statusCodes.includes(response.status) &&
        methodIsRetryable &&
        attempt < retryConfig.limit;

      if (shouldRetry) {
        // Calculate exponential backoff delay
        const delay = Math.min(
          retryConfig.initialDelay * Math.pow(2, attempt),
          retryConfig.backoffLimit
        );

        console.warn(
          `Request failed with status ${response.status}. Retrying in ${delay}ms... (attempt ${attempt + 1}/${retryConfig.limit})`
        );

        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // Success or non-retryable error
      return response;
    } catch (error) {
      lastError = error as Error;

      // Network error - retry if we have attempts left. A request that died in flight may
      // still have been executed, so a method outside the retry list is never replayed here either.
      if (methodIsRetryable && attempt < retryConfig.limit) {
        const delay = Math.min(
          retryConfig.initialDelay * Math.pow(2, attempt),
          retryConfig.backoffLimit
        );

        console.warn(
          `Network error: ${lastError.message}. Retrying in ${delay}ms... (attempt ${attempt + 1}/${retryConfig.limit})`
        );

        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // Out of retries
      throw lastError;
    }
  }

  // This should never happen, but TypeScript needs it
  throw lastError || new Error('Request failed after retries');
}

/**
 * Per-request switches for ApiClient calls.
 */
interface RequestOptions {
  /**
   * Whether a 5xx response sends the browser to /server-error or /service-unavailable
   * (default: true). A caller that handles the failure itself — it re-reads the state the
   * request may have written — turns it off.
   */
  redirectOnServerError?: boolean;
}

/**
 * True when a failed request tells nothing about whether the server acted on it: a 5xx, a
 * timeout or a network error (anything that is not a 4xx answer from the API).
 */
export function isServerOrNetworkFailure(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status !== 'number' || status >= 500;
}

class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details?: Record<string, unknown> | string;
  public readonly field?: string;
  public readonly timestamp: string;
  public readonly requestId: string;

  constructor(errorResponse: StandardErrorResponse['error'], status: number) {
    super(errorResponse.message);
    this.name = 'ApiError';
    this.code = errorResponse.code;
    this.message = errorResponse.message;
    this.status = status;
    this.details = errorResponse.details;
    this.field = errorResponse.field;
    this.timestamp = errorResponse.timestamp;
    this.requestId = errorResponse.requestId;
  }

  toJSON() {
    return {
      success: false,
      error: {
        code: this.code,
        message: this.message,
        details: this.details,
        field: this.field,
        timestamp: this.timestamp,
        requestId: this.requestId,
      },
    };
  }
}

class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private tokenGetter?: () => string | null;

  constructor(baseURL: string, tokenGetter?: () => string | null) {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
    this.tokenGetter = tokenGetter;
  }

  private getAuthToken(): string | null {
    // If tokenGetter was provided, use it (for Zustand store)
    if (this.tokenGetter) {
      return this.tokenGetter();
    }

    // Fallback: Get token from localStorage (Zustand persist)
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage);
        return parsed.state?.token || null;
      } catch {
        return null;
      }
    }
    return null;
  }

  private getHeaders(includeAuth: boolean = true): Record<string, string> {
    const headers = { ...this.defaultHeaders };

    if (includeAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    // Send user's in-app language as Accept-Language (mapped to backend locale)
    const lang = i18n.language || 'es';
    headers['Accept-Language'] = SHORT_TO_LOCALE[lang] || lang;

    return headers;
  }

  private async handleResponse<T>(response: Response, options: RequestOptions = {}): Promise<T> {
    const { redirectOnServerError = true } = options;
    let data: unknown;

    try {
      data = await response.json();
    } catch {
      // If response is not JSON, create a generic StandardError response
      data = {
        success: false,
        error: {
          code: 'HTTP_ERROR',
          message: `HTTP ${response.status}: ${response.statusText}`,
          timestamp: new Date().toISOString(),
          requestId: response.headers.get('x-request-id') || 'unknown',
        },
      };
    }

    if (!response.ok) {
      // Handle specific error status codes with redirects
      const status = response.status;

      // Public pages that should NOT trigger redirects on auth errors
      const publicPaths = ['/', '/search', '/merchants', '/login', '/register', '/email-verified', '/logged-out', '/test-api', '/unauthorized', '/forbidden'];
      const currentPath = window.location.pathname;
      const isPublicPage = publicPaths.includes(currentPath) || currentPath.startsWith('/error');

      // 401 Unauthorized - redirect to unauthorized page (only for protected pages)
      if (status === 401 && !isPublicPage) {
        window.location.href = '/unauthorized';
      }
      // 403 Forbidden - do NOT redirect; let components handle permission errors gracefully
      // (e.g., TanStack Query error handlers, catch blocks in components)
      // 503 Service Unavailable - redirect to service unavailable page
      else if (redirectOnServerError && status === 503 && window.location.pathname !== '/service-unavailable') {
        window.location.href = '/service-unavailable';
      }
      // 500-502, 504+ Server Error - redirect to server error page
      else if (redirectOnServerError && status >= 500 && status !== 503 && window.location.pathname !== '/server-error') {
        window.location.href = '/server-error';
      }

      // Type guard for error response
      const errorData = data as Record<string, unknown>;

      // Check if we have StandardErrorResponse format
      if (errorData.success === false && errorData.error && typeof errorData.error === 'object') {
        // Backend returned StandardErrorResponse
        throw new ApiError(errorData.error as StandardErrorResponse['error'], response.status);
      }

      // Legacy error format - convert to StandardError
      const legacyError: StandardErrorResponse['error'] = {
        code: (errorData.code as string) || 'UNKNOWN_ERROR',
        message: (errorData.error as string) || (errorData.message as string) || `HTTP ${response.status}: ${response.statusText}`,
        timestamp: new Date().toISOString(),
        requestId: response.headers.get('x-request-id') || 'unknown',
      };
      throw new ApiError(legacyError, response.status);
    }

    // Type guard for successful response
    const responseData = data as Record<string, unknown>;

    // For Core API format: { success: true, data: ..., meta: ... }
    if (responseData.success !== undefined && responseData.data !== undefined && responseData.meta !== undefined) {
      console.log('API Response (Core with meta):', { type: 'core_with_meta', data: responseData.data });
      return responseData.data as T;
    }

    // For Core API format: { success: true, data: ..., pagination?: ... }
    // ResponseMiddleware wraps controller responses, so we unwrap by returning data
    if (responseData.success !== undefined && responseData.data !== undefined) {
      console.log('API Response (Core/PGW):', { type: 'core', data: responseData.data });
      return responseData.data as T;
    }

    // For PGW withdrawal methods API: { success: true, methods: [...] }
    if (responseData.success !== undefined && responseData.methods !== undefined) {
      console.log('API Response (PGW withdrawal methods):', { type: 'pgw_withdrawal_methods', methods: responseData.methods });
      // Return full object with methods array
      return responseData as T;
    }

    // For Charon API: check if data.success exists and is true
    // Эта проверка последняя, т.к. она самая общая
    if (responseData.success !== undefined && responseData.success === true) {
      console.log('API Response (Charon):', { type: 'charon', data: responseData.data });
      // Return only the data property (for consistency with other APIs)
      return responseData.data as T;
    }

    // For direct data responses
    console.log('API Response (direct):', { type: 'direct', data });
    return data as T;
  }

  async get<T>(
    endpoint: string,
    includeAuth: boolean = true,
    options: RequestOptions = {}
  ): Promise<T> {
    const response = await fetchWithRetry(`${this.baseURL}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders(includeAuth),
    });

    return this.handleResponse<T>(response, options);
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    includeAuth: boolean = true,
    options: RequestOptions = {}
  ): Promise<T> {
    const response = await fetchWithRetry(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(includeAuth),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response, options);
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    includeAuth: boolean = true
  ): Promise<T> {
    const response = await fetchWithRetry(`${this.baseURL}${endpoint}`, {
      method: 'PUT',
      headers: this.getHeaders(includeAuth),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    includeAuth: boolean = true
  ): Promise<T> {
    const response = await fetchWithRetry(`${this.baseURL}${endpoint}`, {
      method: 'PATCH',
      headers: this.getHeaders(includeAuth),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    includeAuth: boolean = true
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (includeAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }
    // Do NOT set Content-Type — browser sets multipart boundary automatically
    const response = await fetchWithRetry(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string, includeAuth: boolean = true): Promise<T> {
    const response = await fetchWithRetry(`${this.baseURL}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders(includeAuth),
    });

    return this.handleResponse<T>(response);
  }

  async deleteWithBody<T>(
    endpoint: string,
    body: unknown,
    includeAuth: boolean = true
  ): Promise<T> {
    const response = await fetchWithRetry(`${this.baseURL}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders(includeAuth),
      body: JSON.stringify(body),
    });

    return this.handleResponse<T>(response);
  }
}

// API client instances
// Use relative URL /api/v1 - Vite proxy forwards to Gateway (6850)
// Gateway has prefix: '/api/v1', so routes match /api/v1/auth, /api/v1/profile, etc.

// Create API clients with token getter from shared-auth-ui
const tokenGetter = () => useAuthStore.getState().token;
export const charonAPI = new ApiClient('/api/v1', tokenGetter);
export const coreAPI = new ApiClient('/api/v1', tokenGetter);

// PGW v2 API client - uses empty baseURL since wallet.ts already includes /api/v2 prefix
export const pgwAPI = new ApiClient('', tokenGetter);

// Function to set token getter on API clients (kept for backward compatibility)
export function setTokenGetter(getter: () => string | null) {
  (charonAPI as any).tokenGetter = getter;
  (coreAPI as any).tokenGetter = getter;
  (pgwAPI as any).tokenGetter = getter;
}

export type { ApiResponse, ApiError, RequestOptions };